import type {
  SmartCreationBrowserDraft,
  SmartCreationReviewAdGroup,
  SmartCreationReviewCampaign,
} from "@adpilot/shared";
import { IconPencil, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  ProductThumbnail,
  ProductThumbnailWithStatus,
} from "@/components/shared/product-thumbnail";
import { Button } from "@/components/ui/button";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface TargetDialogHeaderProps {
  plan?: SmartCreationBrowserDraft;
  selectedGroup: SmartCreationReviewAdGroup;
  selectedCampaign: SmartCreationReviewCampaign | undefined;
  strategy: string;
  activeGroup: string;
  laneGroups: SmartCreationReviewAdGroup[];
  campaigns: SmartCreationReviewCampaign[];
  viewLocked: boolean;
  locked: boolean;
  closeDisabled: boolean;
  onEditGroup?(group: SmartCreationReviewAdGroup): void;
  onRequestExit(destination: string): void;
}

export function TargetDialogHeader({
  plan,
  selectedGroup,
  selectedCampaign,
  strategy,
  activeGroup,
  laneGroups,
  campaigns,
  viewLocked,
  locked,
  closeDisabled,
  onEditGroup,
  onRequestExit,
}: TargetDialogHeaderProps) {
  const { t } = useTranslation("smartCreation");
  const selectedProduct = selectedGroup.products[0];
  const selectedProductDetail = plan?.draft.products.find(
    (item) => item.sku === selectedProduct?.sku && item.asin === selectedProduct?.asin,
  );

  return (
    <DialogHeader className="shrink-0 gap-2 border-b border-line px-5 py-3">
      <div className="flex min-w-0 items-center gap-3">
        {selectedProduct && plan ? (
          <ProductThumbnailWithStatus
            product={{
              ...selectedProduct,
              title: selectedProductDetail?.title ?? null,
              imageUrl: selectedProductDetail?.imageUrl ?? null,
            }}
            shopId={plan.shopId}
          />
        ) : (
          <ProductThumbnail imageUrl={null} label="—" size="compact" />
        )}
        <div className="min-w-0 flex-1">
          <DialogTitle className="truncate text-base leading-5">
            {selectedGroup.name.effective}
          </DialogTitle>
          <p className="mt-1 truncate text-xs text-muted-foreground">
            {t("review.parity.campaignContext", {
              name: selectedCampaign?.name.effective ?? "—",
            })}{" "}
            · {selectedGroup.products.map((item) => item.asin).join(" / ")} ·{" "}
            {t(`strategy.${strategy}`)}
          </p>
        </div>
        <Select
          value={activeGroup}
          disabled={viewLocked}
          onValueChange={(value) => {
            if (value && value !== activeGroup) onRequestExit(String(value));
          }}
        >
          <SelectTrigger
            size="sm"
            className="w-48 shrink-0"
            aria-label={t("review.parity.productScope")}
          >
            <SelectValue>{t("review.parity.switchScope")}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {laneGroups.map((item) => (
                <SelectItem key={item.ref.lineageKey} value={item.ref.lineageKey}>
                  {
                    campaigns.find(
                      (campaign) => campaign.ref.lineageKey === item.campaignLineageKey,
                    )?.name.effective
                  }{" "}
                  · {item.name.effective} ·{" "}
                  {item.products.map((product) => `${product.sku} / ${product.asin}`).join(" · ")}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        {onEditGroup ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("review.sheet.editGroup")}
            disabled={locked}
            onClick={() => onEditGroup(selectedGroup)}
          >
            <IconPencil />
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t("review.parity.cancel")}
          disabled={closeDisabled}
          onClick={() => onRequestExit("close")}
        >
          <IconX />
        </Button>
      </div>
    </DialogHeader>
  );
}
